import { createContext, useContext, type ReactNode } from 'react'
import axios from 'axios'

import { zh } from './i18n/messages'

// Bilingual UI (English + Simplified Chinese). We use *natural keys*: the English string IS the
// key. English therefore needs no dictionary (a lookup miss returns the key verbatim), and any
// untranslated string visibly falls back to English under zh — which is also how we audit
// completeness. The active locale arrives from Rails as the Inertia `locale` shared prop and is
// pinned into the provider once per full page load (the switcher hard-reloads — see setLocale).
export type Locale = 'en' | 'zh'

const DICTIONARIES: Record<Locale, Record<string, string>> = { en: {}, zh }

type Vars = Record<string, string | number>
export type TFn = (key: string, vars?: Vars) => string

interface I18nValue {
  locale: Locale
  t: TFn
}

const I18nContext = createContext<I18nValue>({ locale: 'en', t: (k) => k })

// Replace {name} placeholders with the supplied values; unknown placeholders are left intact.
function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template
  return template.replace(/\{(\w+)\}/g, (_, key: string) => (key in vars ? String(vars[key]) : `{${key}}`))
}

export function I18nProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  const dict = DICTIONARIES[locale] ?? {}
  const t: TFn = (key, vars) => interpolate(dict[key] ?? key, vars)
  return <I18nContext.Provider value={{ locale, t }}>{children}</I18nContext.Provider>
}

// Translate a natural-key string for the active locale. `const t = useT()` then `t('Save')`.
export function useT(): TFn {
  return useContext(I18nContext).t
}

export function useLocale(): Locale {
  return useContext(I18nContext).locale
}

// Persist the chosen language to the session, then hard-reload so the new `locale` shared prop
// re-instantiates the provider for the entire app (an Inertia SPA visit would keep the stale one).
export async function setLocale(locale: Locale): Promise<void> {
  await axios.post('/locale', { locale })
  window.location.reload()
}
